import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, GraduationCap, ArrowUpRight, ShieldCheck } from "lucide-react";
import { UNIVERSITIES } from "@/lib/data";
import { fadeUp, stagger, viewport } from "@/lib/motion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function UniCard({ u, featured, onOpen }) {
  return (
    <motion.button
      type="button"
      onClick={onOpen}
      variants={fadeUp}
      whileHover={{ y: -6 }}
      data-testid="university-card"
      className={`group relative rounded-3xl overflow-hidden text-left shadow-[0_10px_40px_rgba(27,25,168,0.12)] focus-visible:ring-2 focus-visible:ring-[#FFE500] focus-visible:outline-none ${
        featured ? "lg:col-span-2 min-h-[340px]" : "min-h-[300px]"
      }`}
    >
      <img
        src={u.img}
        alt={u.tr}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0f0e5c]/95 via-[#1B19A8]/35 to-transparent" />

      <span className="absolute top-5 left-5 inline-flex items-center gap-1.5 text-xs font-semibold text-[#1B19A8] bg-[#FFE500] px-3 py-1.5 rounded-full">
        <MapPin size={13} /> {u.city}
      </span>

      {u.programs?.length ? (
        <span className="absolute top-5 right-5 inline-flex items-center gap-1 text-xs font-medium text-white bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full">
          {u.programs.length} program <ArrowUpRight size={13} />
        </span>
      ) : null}

      <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-7 text-white">
        {u.highlight && (
          <span className="inline-flex items-center gap-1.5 mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-[#1B19A8] bg-[#FFE500] px-2.5 py-1 rounded-full">
            {u.highlight}
          </span>
        )}
        <h3 className={`font-display font-bold leading-tight ${featured ? "text-2xl sm:text-3xl" : "text-xl"}`}>
          {u.tr}
        </h3>
        <p className="text-white/75 text-sm mt-1">{u.name}</p>
        <div className="mt-3 pt-3 border-t border-white/15 flex items-center justify-between">
          <span className="text-sm text-[#FFE500] font-medium">{u.note}</span>
          <span className="text-xs text-white/80 font-semibold whitespace-nowrap ml-3 group-hover:text-[#FFE500] transition-colors">Programları Gör →</span>
        </div>
      </div>
    </motion.button>
  );
}

export default function Universities() {
  const [selected, setSelected] = useState(null);

  return (
    <section id="universiteler" data-testid="universities-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-14">
          <span className="text-sm uppercase tracking-[0.2em] text-[#1B19A8] font-semibold">03 · Anlaşmalı Üniversiteler</span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1a1a1a] mt-4 leading-tight">
            Türkiye&apos;de tanınan üniversiteler.
          </h2>
          <p className="mt-5 text-lg text-[#1a1a1a]/70 leading-relaxed">
            {"Tüm üniversiteler ve uluslararası şubeler (Cardiff, Coventry, De Montfort, Heriot-Watt) fiziksel olarak Kazakistan'dadır. Kazakistan'da okur, İngiliz/İskoç veya Kazak diplomanı alırsın. Programları görmek için karta dokun."}
          </p>
          <div className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-[#1B19A8]/8 border border-[#1B19A8]/15 px-4 py-3">
            <ShieldCheck size={20} className="text-[#1B19A8] shrink-0" />
            <span className="text-sm font-semibold text-[#1B19A8]">
              Tüm üniversitelerin diplomaları Türkiye Cumhuriyeti (YÖK) tarafından tanınmaktadır.
            </span>
          </div>
        </div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {UNIVERSITIES.map((u, i) => (
            <UniCard key={u.name} u={u} featured={i === 0} onOpen={() => setSelected(u)} />
          ))}
        </motion.div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="university-dialog">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 text-xs font-semibold text-[#1B19A8] mb-2">
                  <MapPin size={14} /> {selected.city}
                </div>
                <DialogTitle className="font-display text-2xl leading-tight text-[#1a1a1a]">
                  {selected.tr}
                </DialogTitle>
                <p className="text-sm text-[#1a1a1a]/55">{selected.name}</p>
                {selected.highlight && (
                  <span className="inline-flex w-fit items-center gap-1.5 mt-3 text-xs font-bold uppercase tracking-[0.08em] text-[#1B19A8] bg-[#FFE500] px-3 py-1.5 rounded-full">
                    <ShieldCheck size={13} /> {selected.highlight}
                  </span>
                )}
              </DialogHeader>

              <div className="mt-2">
                <div className="text-sm uppercase tracking-[0.15em] text-[#1B19A8] font-semibold mb-3 flex items-center gap-2">
                  <GraduationCap size={16} /> İngilizce Programlar
                </div>
                <ul className="space-y-2.5 max-h-[45vh] overflow-y-auto pr-1">
                  {(selected.programs || []).map((p, idx) => (
                    <li key={idx} className="rounded-xl bg-[#f7f9fa] border border-black/5 p-3.5" data-testid="program-item">
                      <div className="font-semibold text-[#1a1a1a] text-sm">{p.name}</div>
                      <div className="text-xs text-[#1a1a1a]/55 mt-0.5">{p.meta}</div>
                    </li>
                  ))}
                </ul>
              </div>

              <a
                href="#basvuru"
                onClick={() => setSelected(null)}
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#1B19A8] px-6 py-3 text-sm font-semibold text-white hover:bg-[#141178] active:scale-[0.98] transition-[background-color,transform]"
                data-testid="dialog-apply-btn"
              >
                Bu üniversite için başvur
              </a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
